import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { cx } from './cx'
import { Icon, type IconName } from './Icon'
import { Spinner } from './Spinner'
import styles from './Button.module.css'

type Common = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'm' | 's'
  icon?: IconName
  /** Replaces the label with an 18px spinner and blocks clicks. */
  loading?: boolean
}

/** Icon-only buttons must carry an aria-label. */
export type ButtonProps = Common &
  ({ children: ReactNode } | { children?: undefined; icon: IconName; 'aria-label': string })

export function Button({
  variant = 'primary',
  size = 'm',
  icon,
  loading = false,
  disabled,
  className,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const iconOnly = children === undefined
  return (
    <button
      {...rest}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cx(
        styles.button,
        styles[variant],
        size === 's' && styles.small,
        iconOnly && styles.iconOnly,
        loading && styles.loading,
        className,
      )}
    >
      {icon && <Icon name={icon} size={size === 's' ? 16 : 18} />}
      {children}
      {loading && (
        <span className={styles.spinner}>
          <Spinner size={18} />
        </span>
      )}
    </button>
  )
}
